import {
  Button,
  Callout,
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogOverlay,
  DialogPortal,
  DialogTitle,
  Field,
  FieldError,
  FieldLabel,
  Input,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
  Skeleton,
} from "@/components/ui";
import states from "@/constants/States";
import { TRACKERS } from "@/constants/Trackers";
import { useGetPatient } from "@/features/Patient/network/usePatient";
import { trackEvent } from "@/lib/tracking";
import type { ServiceRequest } from "@curai/js-fhir-tools";
import { zodResolver } from "@hookform/resolvers/zod";
import { useEffect, useState } from "react";
import { Controller, useForm } from "react-hook-form";
import type { z } from "zod";
import { useChangePSC } from "../../network/useChangePSC";
import {
  type PSCLocation,
  type PSCLocationSearchParams,
  useSearchLabPSCLocations,
} from "../../network/useSearchLabPSCLocations";
import { PSCOption } from "./PSCOption";
import { formatZipCode, searchSchema } from "./schema";

export const PSCSearchModal = ({
  open,
  onOpenChange,
  patientId,
  encounterId,
  requestGroupId,
  labNetwork,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  patientId: string;
  encounterId: string;
  requestGroupId: string;
  labNetwork: (typeof ServiceRequest.LAB_NETWORKS)[keyof typeof ServiceRequest.LAB_NETWORKS];
}) => {
  const [params, setParams] = useState<PSCLocationSearchParams | null>(null);
  const [selectedPSC, setSelectedPSC] = useState<PSCLocation | null>(null);

  const {
    data: pscSearchResults,
    isFetching: isFetchingPSCs,
    refetch,
  } = useSearchLabPSCLocations(
    params ?? {
      labNetwork: labNetwork,
      city: undefined,
      state: undefined,
      zip: undefined,
    },
  );

  const { data: patient } = useGetPatient(patientId);

  const homeAddressState = patient?.homeAddress?.state;
  const homeAddressZip = patient?.homeAddress?.postalCode;
  const homeAddressCity = patient?.homeAddress?.city;

  const searchForm = useForm<z.infer<typeof searchSchema>>({
    resolver: zodResolver(searchSchema),
    defaultValues: {
      labNetwork: labNetwork,
      city: homeAddressCity,
      state: homeAddressState,
      zip: formatZipCode(homeAddressZip),
    },
  });

  useEffect(() => {
    if (params) {
      trackEvent(TRACKERS.SEARCH_FOR_PSC);
      refetch();
    }
  }, [params, refetch]);

  const onSubmitSearch = (values: z.infer<typeof searchSchema>) => {
    setSelectedPSC(null);
    setParams({
      labNetwork: values.labNetwork,
      city: values.city,
      state: values.state,
      zip: values.zip,
    });
  };

  const changePSC = useChangePSC({
    patientId,
    encounterId,
    requestGroupId,
  });

  const handleChangePSC = async (pscLocationId?: string) => {
    if (!pscLocationId) return;

    try {
      await changePSC.mutateAsync({
        pscLocationId,
      });
      onOpenChange(false);
    } catch (error) {
      // Do nothing.
    }
  };

  return (
    <Dialog modal={false} open={open} onOpenChange={onOpenChange}>
      <DialogPortal>
        <DialogOverlay />
        <DialogContent className="w-full max-w-3xl h-1/2 top-[33%] flex flex-col">
          <DialogHeader className="h-fit">
            <DialogTitle>Patient Service Center Search</DialogTitle>
            <DialogDescription className="hidden">psc search</DialogDescription>
          </DialogHeader>
          <div className="flex flex-row overflow-hidden gap-8">
            <form
              onSubmit={searchForm.handleSubmit(onSubmitSearch)}
              className="grid grid-cols-2 auto-rows-min gap-2 w-1/3 h-full overflow-y-auto"
            >
              <Controller
                control={searchForm.control}
                name="city"
                render={({ field, fieldState }) => (
                  <Field data-invalid={fieldState.invalid}>
                    <FieldLabel htmlFor="city">City</FieldLabel>
                    <Input
                      {...field}
                      id="city"
                      aria-invalid={fieldState.invalid}
                    />
                    <FieldError errors={[fieldState.error]} />
                  </Field>
                )}
              />
              <Controller
                control={searchForm.control}
                name="state"
                render={({ field, fieldState }) => (
                  <Field data-invalid={fieldState.invalid}>
                    <FieldLabel htmlFor="state">State</FieldLabel>
                    <Select value={field.value} onValueChange={field.onChange}>
                      <SelectTrigger
                        id="state"
                        aria-invalid={fieldState.invalid}
                      >
                        <SelectValue placeholder="Select" />
                      </SelectTrigger>
                      <SelectContent>
                        {states.map(({ name, abbreviation }) => (
                          <SelectItem key={abbreviation} value={abbreviation}>
                            {name}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                    <FieldError errors={[fieldState.error]} />
                  </Field>
                )}
              />
              <Controller
                control={searchForm.control}
                name="zip"
                render={({ field, fieldState }) => (
                  <Field data-invalid={fieldState.invalid}>
                    <FieldLabel htmlFor="zip">ZIP</FieldLabel>
                    <Input
                      {...field}
                      id="zip"
                      type="number"
                      className="appearance-none"
                      aria-invalid={fieldState.invalid}
                    />
                    <FieldError errors={[fieldState.error]} />
                  </Field>
                )}
              />
              <Button
                size="sm"
                type="submit"
                className="w-fit col-span-2"
                variant="secondary"
                isLoading={isFetchingPSCs}
              >
                Search
              </Button>
            </form>
            <div className="flex flex-col w-2/3 gap-2">
              {isFetchingPSCs && <Skeleton className="w-full h-14" />}
              {!isFetchingPSCs && pscSearchResults?.pscs?.length === 0 && (
                <Callout
                  variant="info"
                  title="No Results"
                  description="No Patient Service Centers found near the given location."
                />
              )}
              {!isFetchingPSCs &&
                Boolean(pscSearchResults?.pscs?.length) &&
                pscSearchResults?.pscs.every(
                  (psc) => psc.orderingUnavailableReason,
                ) && (
                  <Callout
                    variant="info"
                    title="No locations available"
                    description="No locations in this area accept electronic orders."
                  />
                )}
              {!isFetchingPSCs && Boolean(pscSearchResults?.pscs?.length) && (
                <>
                  <div className="flex flex-1 flex-col gap-2 overflow-auto">
                    {pscSearchResults?.pscs?.map((psc) => (
                      <PSCOption
                        psc={psc}
                        key={psc.locationId}
                        checked={selectedPSC?.locationId === psc.locationId}
                        onChange={() => setSelectedPSC(psc)}
                      />
                    ))}
                  </div>
                  <Button
                    size="sm"
                    className="w-fit ml-auto"
                    disabled={!selectedPSC}
                    isLoading={changePSC.isPending}
                    onClick={() => {
                      handleChangePSC(selectedPSC?.locationId);
                    }}
                  >
                    Apply
                  </Button>
                </>
              )}
            </div>
          </div>
        </DialogContent>
      </DialogPortal>
    </Dialog>
  );
};
